import { MailOutline } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    setTemplateId,
    setPreHeader,
    setSenderProfileId,
    setSubjectLine,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels, EmailSendInputState } from 'domains/campaigns/types';
import ContentSelector from 'domains/campaigns/components/CustomCampaign/components/ContentSelector';
import FormLayout from 'domains/core/components/FormLayout';
import useTemplates from 'hooks/queries/useTemplates';
import AdditionalContentFields from '../EmailContentStep/AdditionalContentFields';

const validateEmailSend = (send: EmailSendInputState): boolean =>
    !!(
        send.templateId &&
        send.senderProfileId &&
        send.subjectLine?.length >= 3 &&
        (!send.preHeader || send.preHeader.length >= 3)
    );

type Props = {
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    dispatchCampaignAction: React.Dispatch<any>;
    onContinue?: () => void; // New: callback for when user clicks continue
    setContentPreviewId: (id: number) => void;
    setCustomContinueHandler?: (handler: (() => void) | null) => void; // New: for custom continue behavior
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
};

const SharedContentSelectionStep = ({
    campaignState,
    contentPreviewId,
    dispatchCampaignAction,
    onContinue,
    setContentPreviewId,
    setCustomContinueHandler,
    setIsContinueEnabled,
    setNextStep,
}: Props) => {
    // Use first send for form state and validation, but apply changes to both
    const firstSend = campaignState.sends[0];

    // Call all hooks first before any conditional logic
    const { data: templates = [], isLoading: isLoadingTemplates } = useTemplates();

    useEffect(() => {
        // Set up navigation based on whether we have a custom onContinue handler
        if (onContinue && setCustomContinueHandler) {
            // For send-time A/B testing: Set up custom continue handler
            const customHandler = () => {
                onContinue(); // Call the provided callback
            };
            setCustomContinueHandler(customHandler);
        } else {
            // Default behavior: clear custom handler and set next step for normal flow
            if (setCustomContinueHandler) {
                setCustomContinueHandler(null);
            }
            setNextStep(CustomCampaignSteps.SCHEDULE_CURATION);
        }

        return () => {
            if (setCustomContinueHandler) {
                setCustomContinueHandler(null);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onContinue]); // setCustomContinueHandler and setNextStep excluded to prevent infinite loops

    // Check if this is an email send and get the properties
    const isEmailSend = firstSend.channel === CampaignChannels.EMAIL;
    const emailSend = isEmailSend ? (firstSend as EmailSendInputState) : null;
    const { templateId = null } = emailSend || {};

    useEffect(() => {
        if (!isEmailSend) {
            setIsContinueEnabled(false);
            return;
        }

        // Validate that all required fields are filled for both sends
        const send1 = campaignState.sends[0];
        const send2 = campaignState.sends[1];

        const isEmailSend1 = send1.channel === CampaignChannels.EMAIL;
        const isEmailSend2 = send2.channel === CampaignChannels.EMAIL;

        if (!isEmailSend1 || !isEmailSend2) {
            setIsContinueEnabled(false);
            return;
        }

        const emailSend1 = send1 as EmailSendInputState;
        const emailSend2 = send2 as EmailSendInputState;

        const isValidSend1 = validateEmailSend(emailSend1);
        const isValidSend2 = validateEmailSend(emailSend2);

        const isEnabled = isValidSend1 && isValidSend2;
        setIsContinueEnabled(isEnabled);
    }, [campaignState.sends, isEmailSend, setIsContinueEnabled]);

    const handleContentChange = useCallback(
        (contentId: number | null) => {
            if (!isEmailSend) return; // Don't execute if not an email send

            if (!contentId) {
                // Clear content for both sends
                [0, 1].forEach((sendIndex) => {
                    dispatchCampaignAction(setTemplateId(null, sendIndex));
                    dispatchCampaignAction(setSenderProfileId(null, sendIndex));
                    dispatchCampaignAction(setSubjectLine('', sendIndex));
                    dispatchCampaignAction(setPreHeader('', sendIndex));
                });

                setContentPreviewId(null);
                return;
            }

            const selectedTemplate = templates.find((template) => template.id === contentId);
            if (!selectedTemplate) return;

            // Apply same content to both Email-A and Email-B
            [0, 1].forEach((sendIndex) => {
                dispatchCampaignAction(setTemplateId(contentId, sendIndex));
                dispatchCampaignAction(setSenderProfileId(selectedTemplate.senderProfileId, sendIndex));
                dispatchCampaignAction(setSubjectLine(selectedTemplate.subjectLine ?? '', sendIndex));
                dispatchCampaignAction(setPreHeader(selectedTemplate.preHeader ?? '', sendIndex));
            });

            if (contentPreviewId) {
                setContentPreviewId(contentId);
            }
        },
        [contentPreviewId, dispatchCampaignAction, isEmailSend, setContentPreviewId, templates]
    );

    const handlePreviewChange = useCallback(() => {
        setContentPreviewId(contentPreviewId ? null : templateId);
    }, [contentPreviewId, setContentPreviewId, templateId]);

    // Handle error case where this isn't an email send
    if (!isEmailSend) {
        return (
            <FormLayout header="Error" stepName="error" icon={<MailOutline />}>
                <Typography>SharedContentSelectionStep can only be used with email sends</Typography>
            </FormLayout>
        );
    }

    return (
        <FormLayout
            header="select content"
            stepName="email"
            icon={<MailOutline />}
            guidingText="Select the content that will be sent to your audience at two different times for A/B testing. This content will be identical for both Email A and Email B."
        >
            <Stack rowGap={4}>
                <ContentSelector
                    contentType={CampaignChannels.EMAIL}
                    contentId={templateId}
                    contentList={templates}
                    onContentChange={handleContentChange}
                    isPreviewVisible={!!contentPreviewId}
                    onPreviewChange={handlePreviewChange}
                    isLoading={isLoadingTemplates}
                />

                <AdditionalContentFields
                    activeSendIndex={0} // Use first send for form state
                    currentSend={emailSend!} // Non-null assertion safe since we checked isEmailSend above
                    dispatchCampaignAction={dispatchCampaignAction}
                    isSharedMode={true} // Apply changes to both sends
                />
            </Stack>
        </FormLayout>
    );
};

export default SharedContentSelectionStep;
